import useUrlState from '@ahooksjs/use-url-state';
import { ProFormInstance } from '@ant-design/pro-form';
import { useLocalStorageState } from 'ahooks';
import React, { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as uuid from 'uuid';
import { useAppConfig } from '@/config/context';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS,
  MatchMode,
  NUMBER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { FilterEntry } from '@/presentation/components/form/types';
import {
  BUNDLE_TIPS_CASE,
  iconText,
} from '@/presentation/components/hint-text/hintText';
import { enumFilterTips, numberFilterTips, stringFilterTips, versionFilterTips } from '@/presentation/components/issue-list/components/tips';
import { CompareParams, TdsFilterType, TdsForm, TdsFormItem, TdsFormItemType } from '@/presentation/components/tds-form';
import { DEFAULT_EVENT_TIME_RANGE } from '@/presentation/components/tds-form/config';
import { TdsCustomFieldsValue } from '@/presentation/components/tds-form/tds-custom-fields';
import { getInitTimeRange } from '@/presentation/components/tds-form/tds-date-cascader';
import {
  COMMON_TIME_TYPE_OPTIONS,
  ISSUE_TIME_TYPE,
} from '@/presentation/components/tds-form/tds-date-cascader/config';
import { TdsColumnGroup } from '@/presentation/components/tds-form/type';
import {
  customFields2Filter,
  dateCascaderConverter,
  dateCascaderTransformer, standardFieldValue2Kb,
} from '@/presentation/components/tds-form/utils';
import { useTdsFormContext } from '@/presentation/hooks/form';
import { IssueType } from '@/proto/common';
import {
  FieldKey,
  FIELD_LABEL,
  Zone,
  CommonFieldKey,
  StandardCommonFieldKey,
  VERSION_MODE_OPTIONS,
} from '@/proto/field';
import { IFilter } from '@/proto/metric';
import { genStandardCompareToken, genStandardToken, KeyDataType } from '@/proto/token';

export const JavaLeakProblemForm: FC<{
  onTokenChange?: (token: string) => void;
  entry: FilterEntry;
}> = (props) => {
  const { t } = useTranslation();
  console.log('JavaLeakProblemForm', props);
  const formRef = useRef<ProFormInstance>();
  const compareFormRef = useRef<ProFormInstance>();

  const config = useAppConfig();
  const { updateFormContext } = useTdsFormContext();
  const [, setState] = useUrlState<Record<string, any>>({
    token: '',
  });

  const [timeRange, setTimeRange] = useLocalStorageState(
    'memory-java-leak-list-v1',
    {
      defaultValue: DEFAULT_EVENT_TIME_RANGE,
    },
  );

  // @ts-ignore
  const formItems: TdsFormItem[] = [
    {
      type: TdsFormItemType.DateCascader,
      label: t('时间范围'),
      name: StandardCommonFieldKey.TimeRange,
      deleteable: false,
      convertValue: dateCascaderConverter,
      transform: dateCascaderTransformer,
      width: 462,
      fieldProps: {
        maxLimitedDays: 90,
        timeWindowOptions: ISSUE_TIME_TYPE,
        timeTypeOptions: COMMON_TIME_TYPE_OPTIONS,
        showTimeWindow: props.entry === FilterEntry.MemoryJavaLeakList,
      },
      inFilter: true,
      filterType: TdsFilterType.DateCascader,
    },
    {
      type: TdsFormItemType.CharacterInput,
      width: 360,
      label: t('APP版本'),
      name: StandardCommonFieldKey.AppVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.AppVersion,
          fieldScope: Zone.JavaLeak,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      deleteable: false,
      filterName: CommonFieldKey.AppVersion,
      filterType: TdsFilterType.Version,
      batchEditMode: true,
      batchEditTips: versionFilterTips,
    },
    {
      type: TdsFormItemType.FieldSelector,
      name: 'standard_app_version_mode',
      labelTools: [iconText(enumFilterTips)],
      label: t(FIELD_LABEL[FieldKey.AppVersionMode] as string),
      labelString: t(FIELD_LABEL[FieldKey.AppVersionMode] as string),
      inFilter: true,
      filterType: TdsFilterType.EnumString,
      filterName: FieldKey.AppVersionMode,
      fieldProps: {
        mode: 'tags',
        options: VERSION_MODE_OPTIONS,
      },
      group: TdsColumnGroup.ApplicationInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('场景'),
      name: StandardCommonFieldKey.Stage,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Stage,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Stage,
          fieldScope: Zone.JavaLeak,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.ContextInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('厂商'),
      name: StandardCommonFieldKey.Brand,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Brand,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Brand,
          fieldScope: Zone.JavaLeak,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.DeviceInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('进程'),
      name: StandardCommonFieldKey.ProcessName,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.ProcessName,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
        defaultValue: {
          mode: MatchMode.MATCH,
        },
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.ContextInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('系统版本'),
      name: StandardCommonFieldKey.OsVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.OsVersion,
          fieldScope: Zone.JavaLeak,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.OsVersion,
      filterType: TdsFilterType.Version,
      batchEditMode: true,
      batchEditTips: versionFilterTips,
      group: TdsColumnGroup.DeviceInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('SDK版本'),
      name: StandardCommonFieldKey.SdkVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.SdkVersion,
          fieldScope: Zone.JavaLeak,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.SdkVersion,
      filterType: TdsFilterType.Version,
      batchEditMode: true,
      batchEditTips: versionFilterTips,
      group: TdsColumnGroup.ApplicationInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('机型'),
      name: StandardCommonFieldKey.Model,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Model,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Model,
          fieldScope: Zone.JavaLeak,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.DeviceInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('用户ID'),
      name: StandardCommonFieldKey.AccountId,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.AccountId,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.UserInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('设备ID'),
      name: StandardCommonFieldKey.Qimei,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Qimei,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.DeviceInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('启动ID'),
      name: StandardCommonFieldKey.LaunchId,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.LaunchId,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.ContextInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('进程启动ID'),
      name: StandardCommonFieldKey.ProcessLaunchId,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.ProcessLaunchId,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.ContextInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('消息ID'),
      name: StandardCommonFieldKey.ClientIdentify,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.ClientIdentify,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.exceptionInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'Issue ID',
      name: StandardCommonFieldKey.Feature,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Feature,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.exceptionInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      labelTools: [iconText(stringFilterTips)],
      label: t('问题特征'),
      labelString: t('问题特征'),
      name: StandardCommonFieldKey.FuncName,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.FuncName,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
        defaultValue: {
          mode: MatchMode.MATCH,
        },
      },
      group: TdsColumnGroup.exceptionInfo,
    },
    {
      type: TdsFormItemType.NumberInput,
      labelString: t('最大泄露内存(KB)'),
      labelTools: [iconText(numberFilterTips)],
      label: t('最大泄漏内存（KB）'),
      name: 'standard_leak_bytes',
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: 'memory_leak_size',
      filterType: TdsFilterType.Custom,
      filterTransform: (fieldKey, fieldValue) => standardFieldValue2Kb(fieldKey, fieldValue),
      group: TdsColumnGroup.exceptionInfo,
    },
    // end
    {
      type: TdsFormItemType.CharacterInput,
      labelString: 'Bundle ID',
      labelTools: [iconText(BUNDLE_TIPS_CASE)],
      label: FIELD_LABEL[FieldKey.BundleId],
      name: StandardCommonFieldKey.BundleId,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.BundleId,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
      group: TdsColumnGroup.ApplicationInfo,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('实验ID'),
      name: StandardCommonFieldKey.ShiplyAbTestTag,
      filterName: CommonFieldKey.ShiplyAbTestTag,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.ShiplyAbTestTag,
        fieldValue,
        'a11',
      ),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.ShiplyTag,
          fieldScope: Zone.JavaLeak,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
      hidden: !config.enableShiplyAbTestTag,
      group: TdsColumnGroup.Custom,
    },
    {
      type: TdsFormItemType.CharacterInput,
      hidden: !config.enableAbTestTag,
      label: t('业务下钻'),
      name: StandardCommonFieldKey.AbTestTag,
      filterName: CommonFieldKey.ShiplyAbTestTag,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.ShiplyAbTestTag,
        fieldValue,
        'a12',
      ),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.AbTestTag,
          fieldScope: Zone.JavaLeak,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
      group: TdsColumnGroup.Custom,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('个例标签'),
      name: StandardCommonFieldKey.TagsInFilter,
      filterName: CommonFieldKey.TagsInFilter,
      hideInCompare: true,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.TagsInFilter,
        fieldValue,
        'a13',
      ),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.TagsInFilter,
          fieldScope: Zone.JavaLeak,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
      group: TdsColumnGroup.Custom,
    },
    {
      type: TdsFormItemType.CharacterInput,
      name: StandardCommonFieldKey.Rom,
      filterName: CommonFieldKey.Rom,
      label: FIELD_LABEL[FieldKey.Rom],
      inFilter: true,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: FieldKey.Rom,
          fieldScope: Zone.JavaLeak,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      group: TdsColumnGroup.DeviceInfo,
    },
    {
      type: TdsFormItemType.NumberInput,
      labelTools: [iconText(numberFilterTips)],
      label: t('使用时长（秒）'),
      labelString: t('使用时长（秒）'),
      name: StandardCommonFieldKey.StandardLaunchDuration,
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.LaunchDuration,
      filterType: TdsFilterType.Number,
      group: TdsColumnGroup.ContextInfo,
    },
    {
      type: TdsFormItemType.Area,
      inFilter: true,
      name: StandardCommonFieldKey.Area,
      label: t('地域'),
      group: TdsColumnGroup.UserInfo,
      fieldProps: {
        fieldScope: Zone.JavaLeak,
      },
      hideInCompare: true,
      filterType: TdsFilterType.Custom,
      filterName: StandardCommonFieldKey.Area,
      filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
        name: item.name,
        operator: item.operator,
        values: item.values || [],
      })) ?? [],
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: t('自定义维度'),
      name: StandardCommonFieldKey.UserCustom,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.UserCustom,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_MEM_LEAK,
        type: 'customFields',
      },
      filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.UserCustom,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
      group: TdsColumnGroup.Custom,
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: t('自定义字段'),
      name: StandardCommonFieldKey.BizExtendInfo,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.BizExtendInfo,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_MEM_LEAK,
        type: 'customKey',
      },
      filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.BizExtendInfo,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
      group: TdsColumnGroup.Custom,
    },
  ];

  const onCompareFinish = async (values: CompareParams) => {
    const token = await genStandardCompareToken({
      ...values,
      uuid: uuid.v4(),
    });

    updateFormContext(draft => ({
      ...draft,
      token,
      fieldsValue: values.fieldsValue,
      fieldsFilter: values.fieldsFilters,
      compareFieldsValue: values.compareFieldsValue,
      compareFieldsFilter: values.compareFieldsFilters,
    }));

    setState({
      token,
    });

    props.onTokenChange?.(token);
  };

  const onFinish = async (values: Record<string, any>, filters: IFilter[]) => {
    const token = await genStandardToken({
      ...values,
      filters,
      // 在token里每次加一个uuid参数，保证token一直更新，每次点查询都会触发新的查询
      fetchUUID: uuid.v4(),
    });
    updateFormContext(draft => ({
      ...draft,
      token,
      fieldsValue: values,
      fieldsFilter: filters,
    }));

    setTimeRange(values?.[StandardCommonFieldKey.TimeRange] ?? DEFAULT_EVENT_TIME_RANGE);
    setState({ token });
    props.onTokenChange?.(token);
  };

  const initialTimeRange = useMemo(() => (timeRange ? {
    ...timeRange,
    date_value: getInitTimeRange(timeRange.time_window_type!, timeRange.short_cut!),
  } : timeRange), [timeRange]);

  return (
    <TdsForm
      items={formItems}
      formRef={formRef}
      compareFormRef={compareFormRef}
      name={'java-leak-list'}
      pageId={PAGE_ID.memory.javaLeak.list}
      onFinish={onFinish}
      onCompareFinish={onCompareFinish}
      initialValues={{
        [StandardCommonFieldKey.TimeRange]: initialTimeRange,
      }}
      enableCompare
      tokenControl={{
        refreshToken: true,
      }}
      renderByGroup
    />
  );
};

